import React, { useMemo } from 'react';
import { Layout, TabPanel, Tabs } from 'tea-component';
import { connect } from 'umi';
import ContentBody from '@op/components/ContentBody';
import { 
  ReportPermissionProvider,
  type TReportOwner,
} from '@op/components/ReportPermission';
import useLanguage from '@op/hooks/useLanguage';
import useTabHistory from '@op/hooks/useTabHistory';
import GenerateSummary from './pages/GenerateSummary';
import './index.scss';
import SummaryList from './pages/SummaryList';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import {
  CustomAISummaryPoint,
  CustomAISummaryListPoint,
} from '@op/operationLog/pages';

const { Content } = Layout;

interface ICustomAISummary {
  gameInfo: any;
  wholeGameInfo: any;
  userInfo: any;
}
const CustomAISummary = (props: ICustomAISummary) => {
  const { gameInfo, wholeGameInfo, userInfo } = props;
  const { formatMessage } = useLanguage();
  const { tab: activeId, onActive } = useTabHistory({
    defaultValue: 'generate',
  });
  const { tab: summaryTab, onActive: onActiveSummaryTab } = useTabHistory<('oneTime' | 'subscriptions')>({
    key: 'listTab',
    defaultValue: 'oneTime',
  });
  const { tab: owner, onActive: onOwnerActive } = useTabHistory<TReportOwner>({
    key: 'owner',
    defaultValue: 'all',
  });

  useInitBuriedPointInfo({
    buriedPoint:
      activeId === 'generate' ? CustomAISummaryPoint : CustomAISummaryListPoint,
  });

  const tabs = useMemo(
    () => [
      {
        id: 'generate',
        label: formatMessage({
          id: 'Generate Summary',
        }),
      },
      {
        id: 'summary',
        label: formatMessage({
          id: 'Summary List',
        }),
      },
    ],
    [formatMessage],
  );

  return (
    <ReportPermissionProvider
      notificationMode={activeId === 'summary' ? 'list' : 'create'}
    >
      <Content className="dbo-page dbo-page-custom-ai-summary">
        <ContentBody
          contentHeard={
            <Content.Header
              className="dbo-header contentHead-normal"
              title={formatMessage({ id: 'Custom AI Summary' })}
              subtitle={
                <span className="dbo-header-subtitle ">
                  {formatMessage({
                    id: 'The AI summary report you have requested for the current game will be displayed on this page. (Excluding keyword and URL analysis)',
                  })}
                </span>
              }
            >
              <div className="header-tab">
                <Tabs
                  ceiling
                  tabs={tabs}
                  animated={false}
                  activeId={activeId}
                  onActive={onActive}
                />
              </div>
            </Content.Header>
          }
        >
          <Tabs
            ceiling
            tabs={tabs}
            animated={false}
            activeId={activeId}
            className="dbo-hide-tabs dbo-header-tab"
            onActive={onActive}
            destroyInactiveTabPanel={false}
          >
            <TabPanel id={'generate'}>
              <GenerateSummary
                userInfo={userInfo}
                gameInfo={gameInfo}
                changeTab={(isSubscript = false) => {
                  onActive({ id: 'summary' });
                  onActiveSummaryTab({ id: isSubscript ? 'subscriptions' : 'oneTime' });
                }}
                wholeGameInfo={wholeGameInfo}
              />
            </TabPanel>
            <TabPanel id={'summary'}>
              <SummaryList
                gameInfo={gameInfo}
                wholeGameInfo={wholeGameInfo}
                needRequest={activeId === 'summary'}
                userInfo={userInfo}
                tab={summaryTab}
                onActive={onActiveSummaryTab}
                owner={owner}
                onOwnerActive={onOwnerActive}
              />
            </TabPanel>
          </Tabs>
        </ContentBody>
      </Content>
    </ReportPermissionProvider>
  );
};

export default connect((state: any) => {
  const { opinionCommon, home } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    userInfo: home.userInfo,
  };
})(CustomAISummary);
